//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { ErrorType } from "@/composables/utils/useErrorBoundary";
import type {
  AdapterInfoResponse,
  DatabaseServerVersionResponse,
  DatabaseVersionResponse,
  LogoFormat,
  ServiceInfoResponse,
} from "../../services/api/domains/service/service.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Service Information & System Management Composable
 *
 * Manages system information, versioning, health checks, and administrative operations.
 * Provides access to Core, Adapter, and database version information.
 *
 * Responsibilities:
 * - Fetch Core application version and configuration
 * - Fetch Adapter application version and status
 * - Retrieve database schema and server versions
 * - Fetch all service health and version information
 * - Retrieve application logo in various formats
 * - Clear Core application cache
 * - Maintain granular loading states per operation
 *
 * @param key - Unique key for state isolation (default: "service")
 */
export const useService = (key = "service") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { withAbort, abortAll } = useAbortableMultiFetch();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const loadingMap = {
    general: useState<boolean>(`${key}.loading.general`, () => false),
    fetchAdapterInfo: useState<boolean>(`${key}.loading.fetchAdapterInfo`, () => false),
    fetchServiceInfo: useState<boolean>(`${key}.loading.fetchServiceInfo`, () => false),
    fetchDatabaseVersion: useState<boolean>(`${key}.loading.fetchDatabaseVersion`, () => false),
    fetchDatabaseServerVersion: useState<boolean>(
      `${key}.loading.fetchDatabaseServerVersion`,
      () => false,
    ),
    fetchAllServiceVersions: useState<boolean>(
      `${key}.loading.fetchAllServiceVersions`,
      () => false,
    ),
    fetchLogo: useState<boolean>(`${key}.loading.fetchLogo`, () => false),
    clearCoreCache: useState<boolean>(`${key}.loading.clearCoreCache`, () => false),
  };

  const error = useState<string | null>(`${key}-error`, () => null);
  const errorType = useState<ErrorType | null>(`${key}-errorType`, () => null);
  const serviceInfo = useState<ServiceInfoResponse | null>(`${key}-info`, () => null);
  const adapterInfo = useState<AdapterInfoResponse | null>(`${key}-adapter-info`, () => null);
  const dbVersion = useState<DatabaseVersionResponse | null>(`${key}-db-version`, () => null);
  const dbServerVersion = useState<DatabaseServerVersionResponse | null>(
    `${key}-db-server-version`,
    () => null,
  );

  //
  // ============================================================================
  // METHODS - VERSION INFORMATION
  // ============================================================================
  //

  /**
   * Fetch Core application information
   *
   * Purpose:
   * Retrieve version, build info, and configuration for Core service.
   * Core is the main backend service for laboratory operations.
   *
   * Responsibilities:
   * - Fetch Core version from API
   * - Update serviceInfo state
   * - Use dedicated loading state (fetchServiceInfo)
   * - Handle request abortion
   *
   * @returns Core service information or null if aborted/error
   */
  const fetchServiceInfo = async (): Promise<ServiceInfoResponse | null> => {
    loadingMap.fetchServiceInfo.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      const response = await withAbort<ServiceInfoResponse>((signal) =>
        cl.service.fetchServiceInfo(signal),
      );

      if (response) {
        serviceInfo.value = response;
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchServiceInfo",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingMap.fetchServiceInfo.value = false;
    }
  };

  /**
   * Fetch Adapter application information
   *
   * Purpose:
   * Retrieve version and status for Adapter service.
   * Adapter handles instrument communication and data transformation.
   *
   * Responsibilities:
   * - Fetch Adapter version from API
   * - Update adapterInfo state
   * - Use dedicated loading state (fetchAdapterInfo)
   * - Handle request abortion
   *
   * @returns Adapter service information or null if aborted/error
   */
  const fetchAdapterInfo = async (): Promise<AdapterInfoResponse | null> => {
    loadingMap.fetchAdapterInfo.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      const response = await withAbort<AdapterInfoResponse>((signal) =>
        cl.service.fetchAdapterInfo(signal),
      );

      if (response) {
        adapterInfo.value = response;
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchAdapterInfo",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingMap.fetchAdapterInfo.value = false;
    }
  };

  /**
   * Fetch internal database schema version
   *
   * Purpose:
   * Retrieve current version of database schema/structure.
   * Tracks database migrations and schema evolution.
   *
   * Responsibilities:
   * - Fetch database version from API
   * - Update dbVersion state
   * - Use dedicated loading state (fetchDatabaseVersion)
   * - Handle request abortion
   *
   * @returns Database schema version or null if aborted/error
   */
  const fetchDatabaseVersion = async (): Promise<DatabaseVersionResponse | null> => {
    loadingMap.fetchDatabaseVersion.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      const response = await withAbort<DatabaseVersionResponse>((signal) =>
        cl.service.fetchDatabaseVersion(signal),
      );

      if (response) {
        dbVersion.value = response;
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchDatabaseVersion",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingMap.fetchDatabaseVersion.value = false;
    }
  };

  /**
   * Fetch database server version
   *
   * Purpose:
   * Retrieve version of database server software (SQL Server, PostgreSQL, etc.).
   * Separate from schema version - this is the database engine version.
   *
   * Responsibilities:
   * - Fetch DB server version from API
   * - Update dbServerVersion state
   * - Use dedicated loading state (fetchDatabaseServerVersion)
   * - Handle request abortion
   *
   * @returns Database server version or null if aborted/error
   */
  const fetchDatabaseServerVersion = async (): Promise<DatabaseServerVersionResponse | null> => {
    loadingMap.fetchDatabaseServerVersion.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      const response = await withAbort<DatabaseServerVersionResponse>((signal) =>
        cl.service.fetchDatabaseServerVersion(signal),
      );

      if (response) {
        dbServerVersion.value = response;
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchDatabaseServerVersion",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingMap.fetchDatabaseServerVersion.value = false;
    }
  };

  /**
   * Fetch all service versions and health status
   *
   * Purpose:
   * Retrieve version and health information for all known services in the system.
   * Provides comprehensive system status overview.
   *
   * Responsibilities:
   * - Fetch info from multiple service endpoints
   * - Aggregate version information
   * - Check health status for each service
   * - Use dedicated loading state (fetchAllServiceVersions)
   * - Handle partial failures gracefully
   *
   * @returns Array of service info with health status
   */
  const fetchAllServiceVersions = async (): Promise<
    Array<{
      id: string;
      name: string;
      icon: string;
      infoUrl: string;
      healthUrl: string;
      appUrl?: string;
      openApiUrl?: string;
      version: string;
      description: string;
      healthStatus: string;
    }>
  > => {
    loadingMap.fetchAllServiceVersions.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const config = useRuntimeConfig();
      const baseUrl = config.public.APP_BASE_URL || "/";

      const appBaseUrl = pathCombine(window.location.origin, baseUrl);
      const coreBaseUrl = config.public.API_BASE_URL.replace(/\/api$/, "");

      const services = [
        {
          id: "fe",
          name: "Lab Portal",
          icon: pathCombine(appBaseUrl, "api/service/logo/svg.svg"),
          infoUrl: pathCombine(appBaseUrl, "api/service/info.json"),
          healthUrl: pathCombine(appBaseUrl, "api/health.json"),
          appUrl: appBaseUrl,
        },
        {
          id: "core",
          name: "Lab Portal API",
          icon: pathCombine(coreBaseUrl, "api/service/logo/svg"),
          infoUrl: pathCombine(coreBaseUrl, "api/service/info"),
          healthUrl: pathCombine(coreBaseUrl, "api/health"),
          openApiUrl: pathCombine(coreBaseUrl, "swagger/app/swagger.json"),
        },
      ];

      const results = await Promise.all(
        services.map(async (svc) => {
          try {
            const [info, health] = await Promise.all([
              fetch(svc.infoUrl).then((r) => r.json()),
              fetch(svc.healthUrl).then((r) => r.json()),
            ]);
            return {
              ...svc,
              ...info,
              healthStatus: health.status || "unknown",
            };
          } catch (err) {
            console.warn(`Failed to fetch ${svc.name}:`, err);
            return { ...svc, version: "unknown", healthStatus: "unhealthy" };
          }
        }),
      );

      return results;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchAllServiceVersions",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      error.value = result.message;
      errorType.value = result.type;
      return [];
    } finally {
      loadingMap.fetchAllServiceVersions.value = false;
    }
  };

  //
  // ============================================================================
  // METHODS - BRANDING & ADMINISTRATION
  // ============================================================================
  //

  /**
   * Fetch application logo in specified format
   *
   * Purpose:
   * Retrieve branding logo for display in UI.
   * Supports multiple formats for different use cases.
   *
   * Responsibilities:
   * - Fetch logo blob from API
   * - Convert blob to Object URL for img src
   * - Use dedicated loading state (fetchLogo)
   * - Handle request abortion
   *
   * @param format - Logo format: "svg" (default) or "png"
   * @returns Object URL for logo image or null if aborted/error
   */
  const fetchLogo = async (format: LogoFormat = "svg"): Promise<string | null> => {
    loadingMap.fetchLogo.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      const blob = await withAbort<Blob>((signal) => cl.service.fetchLogoByFormat(format, signal));

      if (blob) {
        // Convert Blob to Object URL for use in <img> src
        return URL.createObjectURL(blob);
      }

      return null;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchLogo",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message;
      errorType.value = result.type;
      return null;
    } finally {
      loadingMap.fetchLogo.value = false;
    }
  };

  /**
   * Clear Core application cache
   *
   * Purpose:
   * Force invalidation of Core service internal caches.
   * Administrative operation to resolve stale data issues.
   *
   * Responsibilities:
   * - Send cache clear request to Core API
   * - Use dedicated loading state (clearCoreCache)
   * - Return success/failure status
   * - Handle errors gracefully
   *
   * Note: This clears Core cache, not browser/frontend cache
   *
   * @returns True if cache cleared successfully, false if error
   */
  const clearCoreCache = async (): Promise<boolean> => {
    loadingMap.clearCoreCache.value = true;
    error.value = null;
    errorType.value = null;

    try {
      const cl = await useProdClient();
      await cl.service.clearCoreCache();
      return true;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "clearCoreCache",
        fallbackKey: "components.common.errors.operationFailed",
      });
      error.value = result.message;
      errorType.value = result.type;
      return false;
    } finally {
      loadingMap.clearCoreCache.value = false;
    }
  };

  return {
    // State
    loadingMap,
    error: readonly(error),
    errorType: readonly(errorType),
    serviceInfo: readonly(serviceInfo),
    adapterInfo: readonly(adapterInfo),
    dbVersion: readonly(dbVersion),
    dbServerVersion: readonly(dbServerVersion),

    // Methods
    fetchServiceInfo,
    fetchAdapterInfo,
    fetchDatabaseVersion,
    fetchDatabaseServerVersion,
    fetchAllServiceVersions,
    fetchLogo,
    clearCoreCache,
    abortAll,
  };
};
